import { useState, useCallback } from 'react';
import { api, formatCurrency } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import QrScannerModal from '../components/QrScannerModal';
import { Camera, Check, Gift, Search, AlertTriangle, XCircle, Clock, CircleDot, ArrowLeft, QrCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_BON = {
  CREE: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-400/15', label: 'Cree' },
  EN_ATTENTE: { icon: Clock, color: 'text-orange-400', bg: 'bg-orange-400/15', label: 'En attente' },
  UTILISE: { icon: Check, color: 'text-green-400', bg: 'bg-green-400/15', label: 'Deja utilise' },
  EXPIRE: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/15', label: 'Expire' },
};

export default function ScanUnifie() {
  const [mode, setMode] = useState(null); // null = choose, 'bon' | 'jeu'
  const [showScanner, setShowScanner] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [bon, setBon] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  // ── Bon Cadeau Logic ──
  const lookupBon = useCallback(async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setBon(null);
    setResult(null);
    try {
      const res = await api.get(`/bons-cadeaux/scan/${encodeURIComponent(code.trim())}`);
      setBon(res.data);
    } catch {
      toast.error('Bon non trouve');
    }
    setLoading(false);
  }, []);

  const validateBon = async () => {
    if (!bon) return;
    setValidating(true);
    try {
      const res = await api.put(`/bons-cadeaux/${bon.id}/valider`);
      if (res.data.error) {
        setResult({ success: false, message: res.data.error });
        toast.error(res.data.error);
      } else {
        setResult({ success: true, message: res.data.message });
        toast.success(res.data.message);
        setBon({ ...bon, statut: 'UTILISE' });
      }
    } catch {
      setResult({ success: false, message: 'Erreur lors de la validation' });
      toast.error('Erreur');
    }
    setValidating(false);
  };

  // ── Jeu / Voucher Logic ──
  const lookupVoucher = useCallback(async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setVoucher(null);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/roue/voucher/${encodeURIComponent(code.trim())}`);
      if (res.ok) {
        const data = await res.json();
        setVoucher(data);
      } else {
        toast.error('Voucher non trouve');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
    setLoading(false);
  }, []);

  const validateVoucher = async () => {
    if (!voucher) return;
    setValidating(true);
    try {
      const res = await fetch(`${API_URL}/api/roue/voucher/${voucher.code}/redeem`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setResult({ success: true, message: data.message || 'Voucher utilise !' });
        toast.success('Voucher valide !');
        setVoucher({ ...voucher, used: true });
      } else {
        setResult({ success: false, message: data.detail || 'Erreur' });
        toast.error(data.detail || 'Erreur');
      }
    } catch {
      setResult({ success: false, message: 'Erreur de connexion' });
    }
    setValidating(false);
  };

  // ── Scan Handler ──
  const handleScan = useCallback((decodedText) => {
    setShowScanner(false);
    setManualCode(decodedText);
    if (mode === 'bon') {
      lookupBon(decodedText);
    } else {
      lookupVoucher(decodedText);
    }
  }, [mode, lookupBon, lookupVoucher]);

  const handleManualSearch = () => {
    if (mode === 'bon') lookupBon(manualCode);
    else lookupVoucher(manualCode);
  };

  const reset = () => {
    setBon(null);
    setVoucher(null);
    setResult(null);
    setManualCode('');
  };

  const backToChoice = () => {
    setMode(null);
    reset();
  };

  const canValidateBon = bon && (bon.statut === 'CREE' || bon.statut === 'EN_ATTENTE');
  const statutInfo = bon ? STATUT_BON[bon.statut] || STATUT_BON.CREE : null;

  // ══════════ MODE SELECTION ══════════
  if (!mode) {
    return (
      <div className="min-h-screen bg-[#0A0F1E] flex flex-col items-center justify-center px-4" data-testid="scan-unifie-page">
        <Toaster position="top-center" richColors />
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C9A96E]/20 to-[#C9A96E]/5 flex items-center justify-center mb-4 border border-[#C9A96E]/20">
            <QrCode size={28} className="text-[#C9A96E]" />
          </div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Le Mas</h1>
          <p className="text-sm text-[#8892A8] mt-1">Scanner & Valider</p>
        </div>

        <div className="w-full max-w-sm space-y-3">
          <button
            onClick={() => setMode('bon')}
            className="w-full p-5 bg-[#111827] rounded-2xl border border-[#1E2740] hover:border-[#C9A96E]/40 transition-all group"
            data-testid="choose-bon-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/10 flex items-center justify-center group-hover:bg-[#C9A96E]/20 transition-colors">
                <Gift size={22} className="text-[#C9A96E]" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Bon Cadeau</p>
                <p className="text-[#5A6478] text-xs mt-0.5">Scanner et valider un bon cadeau</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setMode('jeu')}
            className="w-full p-5 bg-[#111827] rounded-2xl border border-[#1E2740] hover:border-[#F48FB1]/40 transition-all group"
            data-testid="choose-jeu-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F48FB1]/10 flex items-center justify-center group-hover:bg-[#F48FB1]/20 transition-colors">
                <CircleDot size={22} className="text-[#F48FB1]" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Jeu Fidelite</p>
                <p className="text-[#5A6478] text-xs mt-0.5">Scanner un voucher de la Roue du Mas</p>
              </div>
            </div>
          </button>
        </div>

        <p className="mt-auto pt-8 text-[10px] text-[#3A4560]">Le Mas — Validation interne</p>
      </div>
    );
  }

  const isBon = mode === 'bon';
  const accentColor = isBon ? '#C9A96E' : '#F48FB1';
  const item = isBon ? bon : voucher;

  // ══════════ SCAN & VALIDATE ══════════
  return (
    <div className="min-h-screen bg-[#0A0F1E] flex flex-col items-center px-4 py-8" data-testid={`scan-${mode}-page`}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="w-full max-w-sm">
        <button onClick={backToChoice} className="flex items-center gap-1.5 text-[#5A6478] hover:text-white text-xs mb-4 transition-colors" data-testid="back-to-choice">
          <ArrowLeft size={14} /> Retour
        </button>
      </div>

      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 border" style={{ background: accentColor + '15', borderColor: accentColor + '30' }}>
          {isBon ? <Gift size={28} style={{ color: accentColor }} /> : <CircleDot size={28} style={{ color: accentColor }} />}
        </div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">Le Mas</h1>
        <p className="text-sm text-[#8892A8] mt-1">{isBon ? 'Validation Bon Cadeau' : 'Validation Voucher Jeu'}</p>
      </div>

      {/* Scanner */}
      {!item && (
        <div className="w-full max-w-sm space-y-4">
          <Button
            onClick={() => setShowScanner(true)}
            className="w-full h-14 text-[#0A0F1E] hover:opacity-90 rounded-xl text-base font-medium"
            style={{ background: accentColor }}
            data-testid="open-scanner-btn"
          >
            <Camera size={20} className="mr-2" /> Scanner le QR Code
          </Button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#1E2740]" />
            <span className="text-[10px] text-[#5A6478] uppercase tracking-widest">ou</span>
            <div className="flex-1 h-px bg-[#1E2740]" />
          </div>

          <div className="flex gap-2">
            <Input
              value={manualCode}
              onChange={e => setManualCode(e.target.value)}
              placeholder={isBon ? 'N du bon (ex: LM-202604-...)' : 'Code voucher'}
              className="bg-[#111827] border-[#1E2740] text-white placeholder:text-[#3A4560]"
              onKeyDown={e => e.key === 'Enter' && handleManualSearch()}
              data-testid="manual-code-input"
            />
            <Button
              onClick={handleManualSearch}
              disabled={loading || !manualCode.trim()}
              className="text-[#0A0F1E] px-4"
              style={{ background: accentColor }}
              data-testid="search-btn"
            >
              <Search size={18} />
            </Button>
          </div>
        </div>
      )}

      {loading && <div className="mt-8 text-[#8892A8] text-sm animate-pulse">Recherche en cours...</div>}

      {/* ── BON CADEAU RESULT ── */}
      {isBon && bon && (
        <div className="w-full max-w-sm mt-2 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="bon-result">
          <div className="bg-[#111827] rounded-2xl border border-[#1E2740] overflow-hidden">
            <div className="text-center py-6 border-b border-[#1E2740]">
              <p className="text-5xl font-bold text-[#C9A96E] font-mono">{bon.montant}€</p>
              <p className="text-xs text-[#5A6478] mt-2 font-mono">{bon.numero}</p>
            </div>
            <div className="px-4 py-3 flex items-center justify-center gap-2 border-b border-[#1E2740]">
              {statutInfo && (
                <span className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${statutInfo.bg} ${statutInfo.color}`} data-testid="bon-statut">
                  <statutInfo.icon size={14} /> {statutInfo.label}
                </span>
              )}
            </div>
            <div className="px-4 py-3 space-y-2 text-sm">
              {bon.beneficiaire && <div className="flex justify-between"><span className="text-[#5A6478]">Beneficiaire</span><span className="text-white font-medium">{bon.beneficiaire}</span></div>}
              {bon.client_nom && <div className="flex justify-between"><span className="text-[#5A6478]">Offert par</span><span className="text-[#8892A8]">{bon.client_nom}</span></div>}
              <div className="flex justify-between"><span className="text-[#5A6478]">Expire le</span><span className="text-[#8892A8] font-mono">{bon.date_expiration}</span></div>
              {bon.message && <p className="text-xs italic text-[#C9A96E]/70 pt-1 text-center">"{bon.message}"</p>}
            </div>
          </div>

          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${result.success ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`} data-testid="validation-result">
              {result.success ? <Check size={18} /> : <AlertTriangle size={18} />}
              <span>{result.message}</span>
            </div>
          )}

          <div className="space-y-2">
            {canValidateBon && !result?.success && (
              <Button onClick={validateBon} disabled={validating}
                className="w-full h-12 bg-green-500 text-white hover:bg-green-600 rounded-xl text-base font-medium"
                data-testid="validate-bon-btn"
              >
                <Check size={20} className="mr-2" /> {validating ? 'Validation...' : 'Valider et encaisser'}
              </Button>
            )}
            {bon.statut === 'UTILISE' && !result && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-orange-400/10 text-orange-400 text-sm">
                <AlertTriangle size={16} /> Ce bon a deja ete utilise{bon.date_utilisation ? ` le ${bon.date_utilisation}` : ''}
              </div>
            )}
            {bon.statut === 'EXPIRE' && !result && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-red-400/10 text-red-400 text-sm">
                <XCircle size={16} /> Ce bon est expire
              </div>
            )}
            <Button onClick={reset} variant="ghost" className="w-full text-[#5A6478] hover:text-white text-sm" data-testid="scan-another-btn">
              <Camera size={16} className="mr-2" /> Scanner un autre
            </Button>
          </div>
        </div>
      )}

      {/* ── VOUCHER JEU RESULT ── */}
      {!isBon && voucher && (
        <div className="w-full max-w-sm mt-2 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="voucher-result">
          <div className="bg-[#111827] rounded-2xl border border-[#1E2740] overflow-hidden">
            <div className="text-center py-6 border-b border-[#1E2740]">
              <p className="text-3xl font-bold text-[#F48FB1]">{voucher.prize || voucher.prize_label || 'Lot'}</p>
              <p className="text-xs text-[#5A6478] mt-2 font-mono">{voucher.code}</p>
            </div>
            <div className="px-4 py-3 flex items-center justify-center gap-2 border-b border-[#1E2740]">
              <span className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${voucher.used ? 'bg-green-400/15 text-green-400' : 'bg-blue-400/15 text-blue-400'}`} data-testid="voucher-statut">
                {voucher.used ? <><Check size={14} /> Utilise</> : <><Clock size={14} /> Valide</>}
              </span>
            </div>
            <div className="px-4 py-3 space-y-2 text-sm">
              {voucher.client_name && <div className="flex justify-between"><span className="text-[#5A6478]">Client</span><span className="text-white font-medium">{voucher.client_name}</span></div>}
              {voucher.expires && <div className="flex justify-between"><span className="text-[#5A6478]">Expire le</span><span className="text-[#8892A8] font-mono">{voucher.expires}</span></div>}
            </div>
          </div>

          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${result.success ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`} data-testid="validation-result">
              {result.success ? <Check size={18} /> : <AlertTriangle size={18} />}
              <span>{result.message}</span>
            </div>
          )}

          <div className="space-y-2">
            {!voucher.used && !result?.success && (
              <Button onClick={validateVoucher} disabled={validating}
                className="w-full h-12 bg-green-500 text-white hover:bg-green-600 rounded-xl text-base font-medium"
                data-testid="validate-voucher-btn"
              >
                <Check size={20} className="mr-2" /> {validating ? 'Validation...' : 'Valider le voucher'}
              </Button>
            )}
            {voucher.used && !result && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-orange-400/10 text-orange-400 text-sm">
                <AlertTriangle size={16} /> Ce voucher a deja ete utilise
              </div>
            )}
            <Button onClick={reset} variant="ghost" className="w-full text-[#5A6478] hover:text-white text-sm" data-testid="scan-another-btn">
              <Camera size={16} className="mr-2" /> Scanner un autre
            </Button>
          </div>
        </div>
      )}

      <QrScannerModal open={showScanner} onClose={() => setShowScanner(false)} onScan={handleScan} />
      <p className="mt-auto pt-8 text-[10px] text-[#3A4560]">Le Mas — Validation interne</p>
    </div>
  );
}
